import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, FileText, Banknote, UserCog, BarChart3, LogOut,
  Menu, X, Shield, DollarSign, ShieldCheck, UserCheck, Lock, Ticket,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { employeeMe } from "@/api";

import AdminUsers from "../admin/AdminUsers";
import AdminUserDetail from "../admin/AdminUserDetail";
import AdminApplications from "../admin/AdminApplications";
import AdminDisbursements from "../admin/AdminDisbursements";
import AdminAgents from "../admin/AdminAgents";
import AdminCommissions from "../admin/AdminCommissions";
import AdminReports from "../admin/AdminReports";
import AdminAadhaarQueue from "../admin/AdminAadhaarQueue";
import EmployeeMyReferrals from "./EmployeeMyReferrals";
import EmployeeOverview from "./EmployeeOverview";

// Maps permission keys to their nav metadata. Only items whose permission is
// true in the logged-in employee's permissions map will be shown & routable.
const SECTIONS = [
  { path: "overview",       label: "Overview",           icon: LayoutDashboard, perm: null,                     component: EmployeeOverview },
  { path: "my-agents",      label: "My Agent Referrals", icon: Ticket,          perm: null,                     component: EmployeeMyReferrals },
  { path: "users",          label: "Customers",          icon: Users,           perm: "view_customers",         component: AdminUsers },
  { path: "aadhaar-queue",  label: "KYC / Aadhaar",      icon: ShieldCheck,     perm: "verify_kyc",             component: AdminAadhaarQueue },
  { path: "applications",   label: "Applications",       icon: FileText,        perm: "view_applications",      component: AdminApplications },
  { path: "disbursements",  label: "Disbursements",      icon: Banknote,        perm: "view_disbursements",     component: AdminDisbursements },
  { path: "agents",         label: "Agents",             icon: UserCog,         perm: "view_agents",            component: AdminAgents },
  { path: "commissions",    label: "Commissions",        icon: DollarSign,      perm: "view_commissions",       component: AdminCommissions },
  { path: "reports",        label: "Reports",            icon: BarChart3,       perm: "view_reports",           component: AdminReports },
];

function AccessDenied() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Lock className="h-8 w-8 text-red-500" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Access Denied
        </h2>
        <p className="text-sm text-slate-600">
          You don't have permission to view this section. If you believe this is a mistake, please contact your administrator.
        </p>
      </div>
    </div>
  );
}

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [employee, setEmployee] = useState(user);
  const [permissions, setPermissions] = useState(user?.permissions || {});

  useEffect(() => {
    // Always refresh from server on mount in case permissions changed.
    (async () => {
      try {
        const res = await employeeMe();
        if (res.data?.employee) {
          setEmployee(res.data.employee);
          setPermissions(res.data.employee.permissions || {});
        }
      } catch { /* keep cached */ }
    })();
  }, []);

  const handleLogout = () => {
    logout();
    toast.success("Logged out");
    navigate('/employee-login', { replace: true });
  };

  // Only show nav items for which the employee has permission.
  // The Overview item has perm=null and is always visible.
  const visibleSections = SECTIONS.filter((s) => s.perm === null || permissions[s.perm]);
  const currentPath = location.pathname.replace('/employee/', '').replace('/employee', '') || 'overview';

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transition-transform duration-300 flex flex-col`}
      >
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1565C0] to-[#0D47A1] flex items-center justify-center shadow-md">
              <UserCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-black text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Employee</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Portal</div>
            </div>
          </div>
          <button className="lg:hidden" onClick={() => setSidebarOpen(false)}><X className="h-5 w-5" /></button>
        </div>

        <div className="p-3 border-b border-slate-100">
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-3">
            <div className="text-xs font-bold text-slate-900 truncate" data-testid="employee-name">{employee?.name || '—'}</div>
            <div className="text-[10px] text-slate-500 font-mono">{employee?.employee_id || ''}</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleSections.map((s) => {
            const Icon = s.icon;
            const active = currentPath === s.path || (s.path === 'overview' && currentPath === '');
            return (
              <button
                key={s.path}
                onClick={() => { navigate(`/employee/${s.path}`); setSidebarOpen(false); }}
                data-testid={`nav-${s.path}`}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? 'bg-[#1565C0] text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <Icon className="h-4 w-4" /> {s.label}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
            data-testid="employee-logout"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)}><Menu className="h-5 w-5" /></button>
          <span className="text-sm font-bold">Employee Portal</span>
          <span className="w-5" />
        </div>
        <div className="p-5 lg:p-8 max-w-7xl mx-auto">
          <Routes>
            <Route index element={<Navigate to="overview" replace />} />
            {SECTIONS.map((s) => {
              const allowed = s.perm === null || permissions[s.perm];
              const Component = s.component;
              return (
                <Route
                  key={s.path}
                  path={s.path}
                  element={allowed ? <Component /> : <AccessDenied />}
                />
              );
            })}
            {/* AdminUserDetail accessed from Users list — gated by view_customers */}
            <Route
              path="user/:userId"
              element={permissions.view_customers ? <AdminUserDetail /> : <AccessDenied />}
            />
            {/* Backwards-compat alias — old /employee/my-referrals links still work */}
            <Route path="my-referrals" element={<Navigate to="/employee/my-agents" replace />} />
            <Route path="*" element={<AccessDenied />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
